<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import clsx from 'clsx'
import moment from 'moment'
import Datepicker from 'vue3-datepicker'

import BetaEditorSection from '@/components/beta-editor/BetaEditorSection.vue'
import BetaEditorShell from '@/components/beta-editor/BetaEditorShell.vue'
import PostBetaEditorHeader from '@/components/beta-editor/PostBetaEditorHeader.vue'
import BetaEditorPanelNavigation from '@/components/beta-editor/organisms/BetaEditorPanelNavigation.vue'
import BetaConfirmationModal from '@/components/beta-dashboard/organisms/BetaConfirmationModal.vue'
import BetaModalLayer from '@/components/beta-dashboard/organisms/BetaModalLayer.vue'
import BetaPageSettingsDialog from '@/components/beta-dashboard/organisms/BetaPageSettingsDialog.vue'
import SettingImageSelector from '@/components/beta-editor/settings/controls/BetaSettingImageSelector.vue'
import MediaManagerModal from '@/components/organisms/MediaManagerModal.vue'
import PresetModal from '@/components/organisms/PresetModal.vue'
import TextAiModal from '@/components/organisms/TextAiModal.vue'
import api from '@/services/api'
import modal from '@/services/modal'
import { useAuthStore } from '@/stores/auth'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useDashboardStore } from '@/stores/dashboard'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorCommentStore } from '@/stores/editor/comment'
import { useEditorPageStore } from '@/stores/editor/page'
import { useModalPresetStore } from '@/stores/modal/presets'
import { useSiteStore } from '@/stores/site'
import type { BetaPage, BetaSettingsSection } from '@/types/beta-dashboard'
import {
    betaSettingsSectionForAdvancedRoute,
    type BetaAdvancedSettingsRouteName,
} from '@/utils/beta-settings-navigation'
import { betaPanelQuery } from '@/utils/panel-variant'
import { isExternalLinkPage } from '@/utils/page'

const authStore = useAuthStore()
const betaStore = useEditorBetaStore()
const betaDashboardStore = useBetaDashboardStore()
const commentStore = useEditorCommentStore()
const dashboardStore = useDashboardStore()
const editorPageStore = useEditorPageStore()
const presetsStore = useModalPresetStore()
const siteStore = useSiteStore()
const router = useRouter()

const post = ref<any>(null)
const selectedDate: any = ref(null)
const categoryTitle = ref('')
const pageForSettings = ref<BetaPage | null>(null)

const loadingGetPage = computed(() => editorPageStore.loadingGetPage)
const loadingPorcentGetPage = computed(() => editorPageStore.loadingPorcentGetPage)
const editorDisabled = computed(() => editorPageStore.editorDisabled)
const activePage = computed(() => editorPageStore.activePage)
const blogs = computed(() => betaDashboardStore.pages.filter((page) => page.listPages == 1))
const availableCategories = computed<any[]>(() => (
    Array.isArray(siteStore.categories) ? siteStore.categories : []
))
const selectedCategories = computed<any[]>(() => (
    Array.isArray(editorPageStore.activePage?.categories)
        ? editorPageStore.activePage.categories
        : []
))
const locationAddress = computed({
    get: () => editorPageStore.activePage?.location?.address ?? '',
    set: (address: string) => {
        if (!editorPageStore.activePage) return

        editorPageStore.activePage.location = {
            ...(editorPageStore.activePage.location ?? {}),
            address,
        }
    },
})
const parentPageId = computed(() => {
    const pageId = Number(editorPageStore.activePage?.pages?.[0]?.id)

    return Number.isFinite(pageId) ? pageId : null
})
const focalPointThumbnail = computed(() => (
    editorPageStore.activePage?.thumbnail?.mainImageExtra?.background_image_focal_point ?? { x: 50, y: 50 }
))
const showDate = computed(() => editorPageStore.activePage?.dates?.showDate === 1)

onBeforeMount(async () => {
    void betaDashboardStore.loadNavigationPages()

    betaStore.reset()
    betaStore.setPreviewDevice('desktop')
    betaStore.setPreviewSurface('editor')
    editorPageStore.postStep = 1
    editorPageStore.schedule = false

    const postId = router.currentRoute.value.params.id

    await editorPageStore.getPage(Number(postId))
    await dashboardStore.handleClickPage(post)
    post.value = reactive(editorPageStore.activePage)
    await presetsStore.fetchPresets()
    await commentStore.fetchComments()
})

onMounted(() => {
    authStore.checkAccessType()
})

onBeforeUnmount(() => {
    betaStore.reset()
})

const onChangeThumbnail = (event: any) => {
    if (event.value === null) {
        editorPageStore.thumbnailIsAutoSelect = false
        editorPageStore.activePage.thumbnail = {
            width: 0,
            height: 0,
            mainImage: '',
            mainImageClass: '',
            mainImageExtra: {
                background_image_focal_point: { x: 50, y: 50 },
            },
        }
        return
    }

    if (event.target === 'focal-point') {
        editorPageStore.activePage.thumbnail = {
            ...editorPageStore.activePage.thumbnail,
            mainImageExtra: {
                ...editorPageStore.activePage.thumbnail.mainImageExtra,
                background_image_focal_point: event.value,
            },
        }
        return
    }

    editorPageStore.thumbnailIsAutoSelect = false
    editorPageStore.activePage.thumbnail = {
        width: event.value.width,
        height: event.value.height,
        mainImage: event.value.content,
        mainImageClass: 'center-center',
        mainImageExtra: {
            background_image_focal_point: { x: 50, y: 50 },
        },
    }
}

const toogleAtHome = () => {
    editorPageStore.activePage.isAtHome = editorPageStore.activePage?.isAtHome ? 0 : 1
}

const checkActiveBlog = (id: number) => {
    if (editorPageStore.activePage.pages.length == 0) return false

    return editorPageStore.activePage.pages.find((page: any) => page.id == id) !== undefined
}

const changeBlogParent = (id: number | null) => {
    if (id === null) {
        editorPageStore.activePage.pages = []
        return
    }

    const blogParentExists = editorPageStore.activePage.pages?.find((page: any) => page.id == id)

    if (blogParentExists !== undefined) {
        editorPageStore.activePage.pages = editorPageStore.activePage.pages?.filter((page: any) => page.id !== blogParentExists.id)
        return
    }

    const blogParent = blogs.value.find((blog: any) => blog.id == id)

    if (!blogParent) return

    editorPageStore.activePage.pages.push({
        id: blogParent.id,
        title: blogParent.name,
    })
}

const checkActiveCategory = (title: string) => (
    selectedCategories.value.some((category: any) => category.title === title)
)

const toggleCategory = (category: any) => {
    if (!Array.isArray(editorPageStore.activePage.categories)) {
        editorPageStore.activePage.categories = []
    }

    if (checkActiveCategory(category.title)) {
        editorPageStore.activePage.categories = selectedCategories.value.filter(
            (selectedCategory: any) => selectedCategory.title !== category.title,
        )
        return
    }

    editorPageStore.activePage.categories.push({
        id: category.id ?? null,
        title: category.title,
        ...(category.route_path ? { route_path: category.route_path } : {}),
    })
}

const onAddCategory = async () => {
    const title = categoryTitle.value.trim()

    if (!title) return

    let category = availableCategories.value.find(
        (item: any) => item.title.trim().toLocaleLowerCase() === title.toLocaleLowerCase(),
    )

    if (!category) {
        category = { id: null, title }

        if (!Array.isArray(siteStore.categories)) {
            siteStore.categories = []
        }

        siteStore.categories.push(category)
        await siteStore.refreshSiteData()
    }

    if (!checkActiveCategory(category.title)) {
        toggleCategory(category)
    }

    categoryTitle.value = ''
}

const onChangeShowDate = (value: number) => {
    editorPageStore.activePage.dates.showDate = value
}

const validateSchedulePost = async () => {
    try {
        const today = moment(new Date()).format('YYYY-MM-DD')
        const toPublish = moment(new Date(editorPageStore.activePage.dates?.display_date)).format('YYYY-MM-DD')

        const response = await api.post('date/compare', JSON.stringify({
            first: toPublish,
            seccond: today,
        }))
        const { status, data } = await response.json()

        if (status != '1') throw new Error('Erro ao validar data de publicação')

        editorPageStore.schedule = !!data
    } catch (error) {
        console.log('error', error)
    }
}

watch(() => editorPageStore.postStep, () => {
    if (editorPageStore.postStep == 2 && !selectedDate.value) {
        if (!editorPageStore.activePage.dates?.display_date) {
            selectedDate.value = new Date()
            return
        }

        const split = editorPageStore.activePage.dates?.display_date.split(' ')
        selectedDate.value = new Date(split[0] + 'T00:00:00')
    }

    if (editorPageStore.postStep === 2) {
        editorPageStore.checkThumbnail()
    }

    if (editorPageStore.postStep !== 1) {
        betaStore.clearSelectedSection()
        betaStore.clearSidebarTarget()
    }
}, { immediate: true })

watch(selectedDate, async () => {
    if (!selectedDate.value || !editorPageStore.activePage?.dates) return

    const day = String(selectedDate.value.getDate())
    const month = selectedDate.value.getMonth() + 1 < 10 ? `0${selectedDate.value.getMonth() + 1}` : String(selectedDate.value.getMonth() + 1)
    const year = selectedDate.value.getFullYear()

    editorPageStore.activePage.dates.display_date = `${year}-${month}-${day} 00:00:00`

    await validateSchedulePost()
})

const onClickBetaBackground = () => {
    betaStore.clearSelectedSection()
    betaStore.clearSidebarTarget()
}

const navigateFromPost = async (location: Parameters<typeof router.push>[0]) => {
    const result = await modal.confirmation(
        'Sair do editor?',
        'Essa ação salva e mantém o post atual como rascunho. Continuar?',
        {
            type: 'danger',
            textConfirm: 'Sim, continuar',
            textCancel: 'Continuar editando',
        },
    )

    if (!result) return

    await editorPageStore.publishPost({
        sketch: 1,
        sanitizeBetaPayload: true,
        redirectTo: router.resolve(location).href,
        notification: {
            loading: 'Salvando...',
            success: 'Post salvo com sucesso',
            error: 'Erro ao salvar post',
        },
    })
}

const goDashboard = () => navigateFromPost({
    name: 'dashboard',
    query: betaPanelQuery(),
})

const browsePages = () => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'content',
    },
})

const browseOpportunities = () => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'opportunities',
    },
})

const browseAffiliates = () => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'affiliates',
    },
})

const browseSubscriptions = () => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'subscriptions',
    },
})

const browseHelp = () => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'help',
    },
})

const selectPage = (selectedPage: BetaPage) => {
    if (selectedPage.listPages == 1) {
        return navigateFromPost({
            name: 'dashboard',
            query: {
                ...betaPanelQuery(),
                view: 'content',
                page: selectedPage.id,
            },
        })
    }

    return navigateFromPost({
        name: isExternalLinkPage(selectedPage) ? 'page.external.edit' : 'page.edit',
        params: { id: selectedPage.id },
        query: betaPanelQuery(),
    })
}

const openLayout = () => navigateFromPost({
    name: 'dashboard',
    query: { ...betaPanelQuery(), view: 'layout' },
})

const openSettings = (section: BetaSettingsSection) => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'settings',
        settings: section,
    },
})

const openAdvancedSettings = (routeName: BetaAdvancedSettingsRouteName) => navigateFromPost({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'settings',
        settings: betaSettingsSectionForAdvancedRoute(routeName),
    },
})

const openPageSettings = (selectedPage: BetaPage) => {
    pageForSettings.value = selectedPage
}
</script>

<template>
    <BetaModalLayer>
        <media-manager-modal />
        <preset-modal />
        <BetaConfirmationModal />
        <text-ai-modal />
    </BetaModalLayer>
    <BetaPageSettingsDialog
        :page="pageForSettings"
        @close="pageForSettings = null"
    />

    <div class="post-beta-editor-layout">
        <BetaEditorPanelNavigation
            :pages="betaDashboardStore.pages"
            :current-page-id="parentPageId"
            :loading="betaDashboardStore.loadingContent"
            @home="goDashboard"
            @browse-pages="browsePages"
            @opportunities="browseOpportunities"
            @affiliates="browseAffiliates"
            @subscriptions="browseSubscriptions"
            @help="browseHelp"
            @select-page="selectPage"
            @configure-page="openPageSettings"
            @create-page="browsePages"
            @open-layout="openLayout"
            @open-settings="openSettings"
            @open-advanced-settings="openAdvancedSettings"
        />

        <div class="post-beta-editor-layout__main">
            <beta-editor-shell
                id="post-editor-beta"
                :with-sidebar="editorPageStore.postStep === 1"
                @background-click="onClickBetaBackground"
            >
                <template #menu>
                    <post-beta-editor-header @leave-editor="goDashboard" />
                </template>

                <div class="relative h-auto">
                    <div
                        :class="clsx(
                            loadingGetPage ? 'h-2' : 'h-0',
                            'top-0 bg-[var(--main-color)] transition-all duration-300',
                        )"
                        :style="{ width: `${loadingPorcentGetPage}%` }"
                    />
                </div>

                <TransitionGroup
                    v-if="editorPageStore.postStep == 1 && !loadingGetPage && activePage"
                    name="beta-section-flow"
                    tag="div"
                    class="post-beta-sections"
                >
                    <div
                        v-for="(section, sectionIndex) in activePage.content"
                        :key="section.betaClientKey || section.frameId || section.temporaryId || sectionIndex"
                        class="post-beta-section-flow-item"
                    >
                        <beta-editor-section
                            :section="section"
                            :section-index="sectionIndex"
                            :has-delete="activePage.content.length > 1"
                            :is-editor-disabled="editorDisabled"
                        />
                    </div>
                </TransitionGroup>

                <template v-else-if="editorPageStore.postStep == 2 && activePage">
                    <div class="beta-post-layout">
                        <div class="beta-post-header">
                            <div>
                                <p class="beta-post-header__eyebrow">Configuração e publicação</p>
                                <h2 class="beta-post-header__title">{{ activePage?.title }}</h2>
                                <p class="beta-post-header__description">
                                    Revise os dados abaixo antes de publicar ou agendar o post.
                                </p>
                            </div>

                            <button type="button" class="featured-toggle" @click="toogleAtHome">
                                {{ activePage?.isAtHome ? 'Destaque ativo' : 'Adicionar destaque' }}
                            </button>
                        </div>

                        <div class="beta-post-grid">
                            <section class="beta-card">
                                <h3>Capa</h3>
                                <setting-image-selector
                                    label="Capa"
                                    :value="activePage?.thumbnail.mainImage"
                                    :focal-point="focalPointThumbnail"
                                    with-remove
                                    with-focal-point
                                    @change="onChangeThumbnail"
                                />

                                <small
                                    v-if="editorPageStore.thumbnailIsAutoSelect"
                                    class="beta-card__hint"
                                >
                                    Capa selecionada automaticamente a partir do conteúdo. Você pode alterá-la.
                                </small>
                            </section>

                            <section class="beta-card">
                                <h3>Publicação</h3>
                                <Datepicker
                                    :input-format="'dd/MM/yyyy'"
                                    :placeholder="'DD/MM/AAAA'"
                                    v-model="selectedDate"
                                    class="w-full text-[#060606] font-semibold text-2xl bg-transparent border-none outline-none"
                                />

                                <label class="flex items-center gap-2 text-sm font-semibold text-[#10233E]">
                                    <input
                                        @change="(event: any) => onChangeShowDate(event.target.checked ? 1 : 0)"
                                        :checked="showDate"
                                        type="checkbox"
                                    >
                                    Exibir data de publicação
                                </label>
                            </section>

                            <section class="beta-card beta-card--wide">
                                <h3>Postar conteúdo em</h3>
                                <div class="beta-chip-list">
                                    <button
                                        :class="clsx('beta-chip', activePage?.pages.length == 0 && 'is-active')"
                                        type="button"
                                        @click="changeBlogParent(null)"
                                    >
                                        Nenhum
                                    </button>

                                    <button
                                        v-for="blog in blogs"
                                        :key="blog.id"
                                        :class="clsx('beta-chip', checkActiveBlog(blog.id) && 'is-active')"
                                        type="button"
                                        @click="changeBlogParent(blog.id)"
                                    >
                                        {{ blog.name }}
                                    </button>
                                </div>
                            </section>

                            <section class="beta-card beta-card--wide">
                                <div class="beta-card__heading">
                                    <div>
                                        <h3>Categorias</h3>
                                        <p>Organize o post para facilitar a navegação no site.</p>
                                    </div>

                                    <div class="beta-category-create">
                                        <label class="sr-only" for="beta-post-category">Nova categoria</label>
                                        <input
                                            id="beta-post-category"
                                            v-model="categoryTitle"
                                            type="text"
                                            placeholder="Nova categoria"
                                            @keyup.enter="onAddCategory"
                                        >
                                        <button type="button" :disabled="!categoryTitle.trim()" @click="onAddCategory">
                                            Adicionar
                                        </button>
                                    </div>
                                </div>

                                <div v-if="availableCategories.length" class="beta-chip-list">
                                    <button
                                        v-for="category in availableCategories"
                                        :key="category.id ?? category.title"
                                        :class="clsx('beta-chip', checkActiveCategory(category.title) && 'is-active')"
                                        type="button"
                                        @click="toggleCategory(category)"
                                    >
                                        {{ category.title }}
                                    </button>
                                </div>
                                <p v-else class="beta-card__empty">
                                    Nenhuma categoria criada. Use o campo acima para adicionar a primeira.
                                </p>
                            </section>

                            <section class="beta-card beta-card--wide">
                                <div class="beta-card__heading">
                                    <div>
                                        <h3>Informações complementares</h3>
                                        <p>Esses dados são opcionais e ajudam a contextualizar a publicação.</p>
                                    </div>
                                </div>

                                <div class="beta-post-fields">
                                    <label class="beta-post-field">
                                        <span>Localização</span>
                                        <input
                                            v-model="locationAddress"
                                            type="text"
                                            placeholder="Informe uma localização"
                                        >
                                    </label>

                                    <label class="beta-post-field">
                                        <span>Descrição</span>
                                        <textarea
                                            v-model="activePage.summary"
                                            rows="5"
                                            placeholder="Escreva um breve resumo do post"
                                        />
                                    </label>
                                </div>
                            </section>
                        </div>
                    </div>
                </template>
            </beta-editor-shell>
        </div>
    </div>
</template>

<style scoped>
.post-beta-editor-layout {
    --beta-accent: var(--main-color, #2f86f6);
    min-height: 100vh;
}

.post-beta-editor-layout__main {
    min-width: 0;
    padding-left: 255px;
}

.post-beta-sections {
    @apply relative flex flex-col;
}

.post-beta-section-flow-item {
    @apply min-w-0;
}

.beta-section-flow-move,
.beta-section-flow-enter-active,
.beta-section-flow-leave-active {
    transition:
        transform 1000ms ease-in-out,
        opacity 1000ms ease-in-out,
        filter 1000ms ease-in-out;
}

.beta-section-flow-enter-from,
.beta-section-flow-leave-to {
    opacity: 0;
    filter: blur(1px);
    transform: translateY(18px) scale(0.985);
}

.beta-section-flow-leave-active {
    position: absolute;
    width: 100%;
    pointer-events: none;
}

.beta-post-layout {
    @apply w-full rounded-[28px] border border-[#E3EAF4] bg-white p-8 shadow-[0_24px_60px_rgba(15,23,42,0.08)];
}

.beta-post-header {
    @apply mb-8 flex items-center justify-between gap-6;
}

.beta-post-header__eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.16em] text-[#6C7E99];
}

.beta-post-header__title {
    @apply mt-1 text-2xl font-bold text-[#10233E];
}

.beta-post-header__description {
    @apply mt-2 text-sm leading-6 text-[#6C7E99];
}

.featured-toggle {
    @apply rounded-full border border-[#DCE5F2] bg-[#F9FBFF] px-4 py-2 text-sm font-semibold text-[#315A9A];
}

.beta-post-grid {
    @apply grid grid-cols-1 gap-6 lg:grid-cols-2;
}

.beta-card {
    @apply flex flex-col gap-4 rounded-2xl border border-[#E3EAF4] bg-[#FAFCFF] p-6;
}

.beta-card--wide {
    @apply lg:col-span-2;
}

.beta-card h3 {
    @apply text-base font-bold text-[#10233E];
}

.beta-card__heading {
    @apply flex flex-col justify-between gap-4 md:flex-row md:items-center;
}

.beta-card__heading p,
.beta-card__hint,
.beta-card__empty {
    @apply text-sm leading-6 text-[#6C7E99];
}

.beta-category-create {
    @apply flex w-full gap-2 md:w-auto;
}

.beta-category-create input,
.beta-post-field input,
.beta-post-field textarea {
    @apply rounded-xl border border-[#DCE5F2] bg-white px-4 py-3 text-sm text-[#10233E] outline-none transition-colors;
}

.beta-category-create input {
    @apply min-w-0 flex-1 md:w-56;
}

.beta-category-create input:focus,
.beta-post-field input:focus,
.beta-post-field textarea:focus {
    @apply border-[var(--main-color)];
}

.beta-category-create button {
    @apply rounded-xl bg-[var(--main-color)] px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40;
}

.beta-chip-list {
    @apply flex flex-wrap gap-3;
}

.beta-chip {
    @apply rounded-full border border-[#DCE5F2] bg-white px-4 py-2 text-sm font-semibold text-[#4C5E7A];
}

.beta-chip.is-active {
    @apply border-[var(--main-color)] text-[#10233E];
}

.beta-post-fields {
    @apply grid grid-cols-1 gap-5 lg:grid-cols-2;
}

.beta-post-field {
    @apply flex flex-col gap-2;
}

.beta-post-field span {
    @apply text-sm font-bold text-[#10233E];
}

.beta-post-field textarea {
    @apply min-h-[132px] resize-y;
}

@media (max-width: 1023px) {
    .post-beta-editor-layout__main {
        padding-left: 0;
    }
}
</style>
